
import { Component, ChangeDetectionStrategy, inject, signal, OnInit, ViewChild, ElementRef, AfterViewInit, effect } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { DataService } from '../../services/data.service';
import { ClinicSettings, AppUser } from '../../models';
import { NotificationService } from '../../services/notification.service';
// Removed unused TranslatePipe import

@Component({
  selector: 'app-clinic-settings',
  standalone: true,
  imports: [ReactiveFormsModule, CommonModule], // Removed TranslatePipe
  templateUrl: './clinic-settings.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ClinicSettingsComponent implements OnInit, AfterViewInit {
  private dataService = inject(DataService);
  private notificationService = inject(NotificationService);
  private fb: FormBuilder = inject(FormBuilder);

  @ViewChild('signaturePad') signaturePadElement!: ElementRef<HTMLCanvasElement>;
  private canvasContext: CanvasRenderingContext2D | null = null;
  private isDrawing = false;

  settingsForm: FormGroup;
  isLoading = signal(true);
  isSaving = signal(false);
  statusMessage = signal<{ type: 'success' | 'error', text: string } | null>(null);
  
  doctors = signal<AppUser[]>([]);
  
  // Previews
  logoPreview = signal<string | null>(null);
  bgPreview = signal<string | null>(null);
  signaturePreview = signal<string | null>(null);

  // Available Professions
  professions = [
    { id: 'MODERN', label: 'เวชกรรม (Modern Medicine)' },
    { id: 'TRADITIONAL', label: 'แพทย์แผนไทย (Thai Traditional)' },
    { id: 'APPLIED_TRADITIONAL', label: 'แพทย์แผนไทยประยุกต์ (Applied Thai)' },
    { id: 'DENTISTRY', label: 'ทันตกรรม (Dentistry)' },
    { id: 'PHARMACY', label: 'เภสัชกรรม (Pharmacy)' },
    { id: 'CHINESE', label: 'การแพทย์แผนจีน (Chinese Medicine)' },
    { id: 'FOLK', label: 'หมอพื้นบ้าน (Folk Medicine)' }
  ];

  constructor() {
    this.settingsForm = this.fb.group({
      licenseNumber: [''],
      licenseValidUntil: [''], 
      operatingHours: ['09:00 - 20:00'],
      defaultDoctorId: [null],
      enableTelemedAutoApprove: [false],
      
      // Profession Type (New)
      professionType: ['TRADITIONAL'], // Default to Thai Traditional as it's common for cannabis clinics

      // Visuals
      certificateHeaderStyle: ['NONE'],
      headerText: ['ใบรับรองแพทย์ / Medical Certificate'],
      addressText: [''],
      certificateLogoUrl: [''],
      useCustomTemplate: [false],
      certificateBgUrl: [''],
      doctorSignatureUrl: [''],
      
      // Watermark
      watermarkText: ['ใช้สำหรับขอใบสั่งจ่ายสมุนไพรควบคุม (กัญชา) เท่านั้น']
    });

    // Fix: Watch for loading state change to initialize canvas when it appears in DOM
    effect(() => {
      if (!this.isLoading()) {
        setTimeout(() => {
          this.initCanvas();
        }, 100);
      }
    });
  }

  ngOnInit(): void {
    this.loadData();
  }

  ngAfterViewInit() {
    this.initCanvas();
  }

  initCanvas() {
    if (!this.signaturePadElement) return;
    
    const canvas = this.signaturePadElement.nativeElement;
    this.canvasContext = canvas.getContext('2d');
    
    if (this.canvasContext) {
      this.canvasContext.lineWidth = 2;
      this.canvasContext.lineCap = 'round';
      this.canvasContext.strokeStyle = 'black';
    }

    // Reset Listeners to avoid duplicates if re-initialized
    // Note: Since element is destroyed/recreated by @if, strict removal isn't always needed, but good practice.
    // Here we just add them, as the old element is gone from memory.

    // Event Listeners for Drawing
    canvas.addEventListener('mousedown', (e) => this.startDrawing(e));
    canvas.addEventListener('mousemove', (e) => this.draw(e));
    canvas.addEventListener('mouseup', () => this.stopDrawing());
    canvas.addEventListener('mouseout', () => this.stopDrawing());
    
    // Touch support
    canvas.addEventListener('touchstart', (e) => this.startDrawing(e), { passive: false });
    canvas.addEventListener('touchmove', (e) => this.draw(e), { passive: false });
    canvas.addEventListener('touchend', () => this.stopDrawing());
  }

  startDrawing(event: MouseEvent | TouchEvent) {
    this.isDrawing = true;
    const pos = this.getEventPos(event);
    this.canvasContext?.beginPath();
    this.canvasContext?.moveTo(pos.x, pos.y);
    event.preventDefault(); // Prevent scrolling on touch
  }

  draw(event: MouseEvent | TouchEvent) {
    if (!this.isDrawing) return;
    const pos = this.getEventPos(event);
    this.canvasContext?.lineTo(pos.x, pos.y);
    this.canvasContext?.stroke();
    event.preventDefault();
  }

  stopDrawing() {
    this.isDrawing = false;
    this.canvasContext?.closePath();
  }

  getEventPos(event: MouseEvent | TouchEvent) {
    const canvas = this.signaturePadElement.nativeElement;
    const rect = canvas.getBoundingClientRect();
    let clientX, clientY;
    
    // Robust check for TouchEvent
    if ('touches' in event) {
      clientX = event.touches[0].clientX;
      clientY = event.touches[0].clientY;
    } else {
      clientX = (event as MouseEvent).clientX;
      clientY = (event as MouseEvent).clientY;
    }
    
    return {
      x: clientX - rect.left,
      y: clientY - rect.top
    };
  }

  clearSignature() {
    const canvas = this.signaturePadElement.nativeElement;
    this.canvasContext?.clearRect(0, 0, canvas.width, canvas.height);
  }

  saveSignatureFromPad() {
    const canvas = this.signaturePadElement.nativeElement;
    // Check if canvas is empty
    const blank = document.createElement('canvas');
    blank.width = canvas.width;
    blank.height = canvas.height;
    if (canvas.toDataURL() === blank.toDataURL()) {
        this.notificationService.showWarning("Please sign before saving.");
        return;
    }

    const dataUrl = canvas.toDataURL('image/png');
    this.settingsForm.patchValue({ doctorSignatureUrl: dataUrl });
    this.signaturePreview.set(dataUrl);
    this.clearSignature(); // Clear pad after using
  }

  async loadData(): Promise<void> {
    this.isLoading.set(true);
    try {
      const [settings, users] = await Promise.all([
        this.dataService.getClinicSettings(),
        this.dataService.getUsers()
      ]);
      
      this.doctors.set(users.filter(u => u.role === 'doctor' || u.role === 'admin' || u.role === 'owner'));

      if (settings) {
        this.settingsForm.patchValue(settings);
        this.logoPreview.set(settings.certificateLogoUrl || null);
        this.bgPreview.set(settings.certificateBgUrl || null);
        this.signaturePreview.set(settings.doctorSignatureUrl || null);
        
        // Defaults if missing
        if(!settings.certificateHeaderStyle) this.settingsForm.patchValue({certificateHeaderStyle: 'NONE'});
        if(!settings.headerText) this.settingsForm.patchValue({headerText: 'ใบรับรองแพทย์ / Medical Certificate'});
        if(!settings.professionType) this.settingsForm.patchValue({professionType: 'TRADITIONAL'});
        if(!settings.watermarkText) this.settingsForm.patchValue({watermarkText: 'ใช้สำหรับขอใบสั่งจ่ายสมุนไพรควบคุม (กัญชา) เท่านั้น'});
      }
    } catch (e) {
      this.statusMessage.set({ type: 'error', text: 'Failed to load clinic settings.' });
    } finally {
      this.isLoading.set(false);
    }
  }

  // File Upload Handlers
  onFileSelected(event: Event, field: 'certificateLogoUrl' | 'certificateBgUrl' | 'doctorSignatureUrl'): void {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files[0]) {
      const file = input.files[0];
      const reader = new FileReader();
      reader.onload = (e) => {
        const result = e.target?.result as string;
        this.settingsForm.patchValue({ [field]: result });
        if (field === 'certificateLogoUrl') this.logoPreview.set(result);
        if (field === 'certificateBgUrl') this.bgPreview.set(result);
        if (field === 'doctorSignatureUrl') this.signaturePreview.set(result);
      };
      reader.readAsDataURL(file);
    }
  }

  removeImage(field: 'certificateLogoUrl' | 'certificateBgUrl' | 'doctorSignatureUrl'): void {
      this.settingsForm.patchValue({ [field]: '' });
      if (field === 'certificateLogoUrl') this.logoPreview.set(null);
      if (field === 'certificateBgUrl') this.bgPreview.set(null);
      if (field === 'doctorSignatureUrl') this.signaturePreview.set(null);
  }

  async onSave(): Promise<void> {
    if (this.settingsForm.invalid) return;

    this.isSaving.set(true);
    this.statusMessage.set(null);
    try {
      await this.dataService.updateClinicSettings(this.settingsForm.value as ClinicSettings);
      this.statusMessage.set({ type: 'success', text: 'บันทึกการตั้งค่าคลินิกเรียบร้อยแล้ว' });
      setTimeout(() => this.statusMessage.set(null), 3000);
    } catch (e) {
      this.statusMessage.set({ type: 'error', text: 'Failed to save settings.' });
    } finally {
      this.isSaving.set(false);
    }
  }
}
